import { useCallback, useRef, useState } from 'react';

export interface VideoPreferences {
  micEnabled: boolean;
  cameraEnabled: boolean;
  autoJoin: boolean; // Future: allow disabling auto-join
}

const STORAGE_KEY = 'videoPreferences';

const DEFAULT_PREFERENCES: VideoPreferences = {
  micEnabled: false,
  cameraEnabled: false,
  autoJoin: true
};

/**
 * The user's *desired* media state, persisted in localStorage.
 *
 * This is an intent, not an observation: it is what the next connection will
 * ask the browser for. What is actually being published is only ever known by
 * LiveKit, so anything that reports "you are live" must read the room, not
 * this hook (#75).
 */
export function useVideoPreferences() {
  const [preferences, setPreferences] = useState<VideoPreferences>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : { ...DEFAULT_PREFERENCES };
  });

  // Mirrors state so that two writes in the same tick (set, then revert on a
  // device failure) compose instead of clobbering each other.
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;

  const setPreference = useCallback((key: keyof VideoPreferences, value: boolean) => {
    const updated = { ...preferencesRef.current, [key]: value };
    preferencesRef.current = updated;
    setPreferences(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

    // Note: Future enhancement - sync preferences to backend for persistence across devices
    // This would require a user preferences endpoint to store/retrieve settings
  }, []);

  const setMicEnabled = useCallback(
    (value: boolean) => setPreference('micEnabled', value),
    [setPreference]
  );

  const setCameraEnabled = useCallback(
    (value: boolean) => setPreference('cameraEnabled', value),
    [setPreference]
  );

  return {
    preferences,
    setPreference,
    setMicEnabled,
    setCameraEnabled,
    toggleMic: useCallback(
      () => setPreference('micEnabled', !preferencesRef.current.micEnabled),
      [setPreference]
    ),
    toggleCamera: useCallback(
      () => setPreference('cameraEnabled', !preferencesRef.current.cameraEnabled),
      [setPreference]
    ),
    toggleAutoJoin: useCallback(
      () => setPreference('autoJoin', !preferencesRef.current.autoJoin),
      [setPreference]
    )
  };
}
